/**
 * The soldier's geometry kit (DECISIONS 198): lofts (rings of a few sides, vertex-coloured so a part's tones bake into one
 * draw), side profiles extruded and chamfered (the D guns' style), chamfered boxes, bars. Shared by soldierLooks.ts (the
 * body, the carrier) and helmetLooks.ts (the helmets). Person units, +z front. Rendering only.
 */
import * as THREE from 'three';
import { markShared } from './meshBake';
import type { Kit } from './armourLooks';

/** The chamfer on the plates and pouches (person units). */
export const CC = 0.01;

const matCache = new Map<string, THREE.MeshLambertMaterial>();
export function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
/** The white material a loft's vertex colours ride on (the bake multiplies them in). */
export function vcMat(): THREE.MeshLambertMaterial {
  let m = matCache.get('vc');
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color: '#ffffff', flatShading: true, vertexColors: true })); matCache.set('vc', m); }
  return m;
}
export function mix(a: string, b: string, t: number): string { return '#' + new THREE.Color(a).lerp(new THREE.Color(b), t).getHexString(); }

/** A loft's ring: at height `y`, `rx` × `rz` round (x, z); `c` colours the band from this ring to the next; `lift` raises a point by its angle (0 = front). */
export interface Ring { y: number; rx: number; rz: number; x?: number; z?: number; c?: string; lift?: (a: number) => number;
  /** colours the band from this ring to the next by angle round it (0 = the front), over `c` */
  cAt?: (a: number) => string }

/**
 * A loft along y through `rings` (any order), `sides` round, a flat face to the front; capped at either end where it
 * shows. Faces are turned outward from the rings' centres. Triangles: 2·sides a band, sides − 2 a cap.
 */
export function loftGeometry(rings: Ring[], sides: number, colour: string, capFirst: boolean, capLast: boolean, inward = false): THREE.BufferGeometry {
  const pos: number[] = [], col: number[] = [], c3 = new THREE.Color();
  const phase = Math.PI / sides;
  const pt = (r: Ring, k: number): THREE.Vector3 => {
    const a = phase + (2 * Math.PI * k) / sides;
    return new THREE.Vector3((r.x ?? 0) + r.rx * Math.sin(a), r.y + (r.lift ? r.lift(a) : 0), (r.z ?? 0) + r.rz * Math.cos(a));
  };
  const n = new THREE.Vector3(), e1 = new THREE.Vector3(), e2 = new THREE.Vector3(), out = new THREE.Vector3();
  const tri = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, color: string) => {
    n.crossVectors(e1.subVectors(b, a), e2.subVectors(c, a));
    if ((n.dot(out) < 0) !== inward) [b, c] = [c, b];   // (inward: faces turned in, a lining seen from inside)
    pos.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
    c3.set(color);
    for (let i = 0; i < 3; i++) col.push(c3.r, c3.g, c3.b);
  };
  const pts = rings.map(r => Array.from({ length: sides }, (_, k) => pt(r, k)));
  for (let i = 0; i < rings.length - 1; i++) {
    const color = rings[i].c ?? colour;
    const ax = ((rings[i].x ?? 0) + (rings[i + 1].x ?? 0)) / 2, az = ((rings[i].z ?? 0) + (rings[i + 1].z ?? 0)) / 2;
    for (let k = 0; k < sides; k++) {
      const a = pts[i][k], b = pts[i][(k + 1) % sides], c = pts[i + 1][(k + 1) % sides], d = pts[i + 1][k];
      out.set((a.x + b.x + c.x + d.x) / 4 - ax, 0, (a.z + b.z + c.z + d.z) / 4 - az);   // outward: from the band's axis
      const fc = rings[i].cAt ? rings[i].cAt!(phase + (2 * Math.PI * (k + 0.5)) / sides) : color;
      tri(a, b, c, fc); tri(a, c, d, fc);
    }
  }
  const up = Math.sign(rings[rings.length - 1].y - rings[0].y) || 1;
  const cap = (i: number, toward: number) => {
    const ring = pts[i], color = rings[Math.min(i, rings.length - 2)].c ?? colour;
    out.set(0, toward, 0);
    for (let k = 1; k < sides - 1; k++) tri(ring[0], ring[k], ring[k + 1], color);
  };
  if (capFirst) cap(0, -up);
  if (capLast) cap(rings.length - 1, up);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  geo.computeVertexNormals();
  return geo;
}
export function loft(parent: THREE.Object3D, rings: Ring[], sides: number, colour: string, capFirst = true, capLast = true, inward = false): THREE.Mesh {
  const m = new THREE.Mesh(loftGeometry(rings, sides, colour, capFirst, capLast, inward), vcMat());
  parent.add(m);
  return m;
}
/** A side profile (`pts`: z, y pairs, the front to +z) extruded `w` across x at `x`; `c` > 0 chamfers its edges. */
export function slab(parent: THREE.Object3D, pts: number[], w: number, color: string, x = 0, c = 0): THREE.Mesh {
  const shape = new THREE.Shape();
  shape.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) shape.lineTo(pts[i], pts[i + 1]);
  const depth = w - 2 * c;
  const geo = new THREE.ExtrudeGeometry(shape, c > 0
    ? { depth, bevelEnabled: true, bevelSegments: 1, bevelSize: c, bevelThickness: c, bevelOffset: -c, curveSegments: 1 }
    : { depth, bevelEnabled: false, curveSegments: 1 });
  geo.clearGroups();
  geo.translate(0, 0, -depth / 2);
  geo.rotateY(-Math.PI / 2);
  const m = new THREE.Mesh(geo, mat(color));
  m.position.x = x;
  parent.add(m);
  return m;
}
/** A box, chamfered by `c` (0: a plain box, 12 triangles; chamfered, 28). */
export function cbox(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number, c = 0): THREE.Mesh {
  if (c <= 0) {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
    m.position.set(x, y, z); parent.add(m); return m;
  }
  const m = slab(parent, [-d / 2, -h / 2, d / 2, -h / 2, d / 2, h / 2, -d / 2, h / 2], w, color, 0, Math.min(c, w / 3, h / 3, d / 3));
  m.position.set(x, y, z);
  return m;
}
/** A pouch or a plate: chamfered while whole, else through 67.56's wear (its marks, its tears). */
export function piece(k: Kit, kind: 'pad' | 'plate', parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number, face: 'front' | 'back' | 'left' | 'right'): void {
  if (k.wear === 'whole') cbox(parent, w, h, d, color, x, y, z, CC);
  else k[kind](parent, w, h, d, color, x, y, z, face);
}
/** A bar `thick` square from `a` to `b`. */
export function bar(parent: THREE.Object3D, a: [number, number, number], b: [number, number, number], thick: number, color: string): THREE.Mesh {
  const va = new THREE.Vector3(...a), vb = new THREE.Vector3(...b), d = vb.clone().sub(va);
  const m = new THREE.Mesh(new THREE.BoxGeometry(thick, d.length() + thick * 0.5, thick), mat(color));
  m.position.copy(va).addScaledVector(d, 0.5);
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize());
  parent.add(m);
  return m;
}
